import React, {useContext, useDebugValue, useEffect, useState} from 'react'
import isChromatic from 'chromatic/isChromatic'
import throttle from 'lodash/throttle'
import {memoizeWith} from 'ramda'

import {DeviceCategory} from '@vio/core/src/utils/userAgent/types'

// TODO - refactor this into a theme util
import {
  Breakpoints,
  BreakpointType,
  deviceWidthToBreakpointsMapping
} from '../utils/breakpoints'

interface DeviceLayoutBreakpointsTypes {
  isDesktopXs: boolean
  isDesktopSm: boolean
  isDesktopMd: boolean
  isDesktopLg: boolean
  isMobileSm: boolean
  isMobileLg: boolean
}

export interface DeviceLayoutHookReturnType
  extends DeviceLayoutBreakpointsTypes {
  isMobile: boolean
  isDesktop: boolean
}

export const deviceTypeToBreakpoint = (deviceType: DeviceCategory) => {
  if (deviceType === 'desktop') return Breakpoints.desktopMd
  if (deviceType === 'tablet') return Breakpoints.desktopXs
  if (deviceType === 'mobile') return Breakpoints.mobileLg
  return Breakpoints.desktopMd
}

export const getDeviceBreakpointLayout = () => {
  if (typeof window === 'undefined') return
  return deviceWidthToBreakpointsMapping(window?.innerWidth)
}

export const breakpointToDeviceType = (deviceBreakpoint?: Breakpoints) => {
  if (!deviceBreakpoint) {
    return {
      isMobile: true,
      isDesktop: false
    }
  }

  return {
    isMobile: [Breakpoints.mobileSm, Breakpoints.mobileLg].includes(
      deviceBreakpoint
    ),
    isDesktop: [
      Breakpoints.desktopXs,
      Breakpoints.desktopSm,
      Breakpoints.desktopMd,
      Breakpoints.desktopLg
    ].includes(deviceBreakpoint)
  }
}

// Intentionally NOT useMemo, this will reuse one cache across all useDeviceLayout hook implementations instead of creating a cache for each one
const breakPointToHookValue = memoizeWith(
  (deviceBreakpoint: Breakpoints | undefined) => deviceBreakpoint || 'unknown',
  (deviceBreakpoint: Breakpoints | undefined): DeviceLayoutHookReturnType => {
    const deviceLayout = breakpointToDeviceType(deviceBreakpoint)
    return {
      ...deviceLayout,
      isDesktopXs: deviceBreakpoint === Breakpoints.desktopXs,
      isDesktopSm: deviceBreakpoint === Breakpoints.desktopSm,
      isDesktopMd: deviceBreakpoint === Breakpoints.desktopMd,
      isDesktopLg: deviceBreakpoint === Breakpoints.desktopLg,
      isMobileSm: deviceBreakpoint === Breakpoints.mobileSm,
      isMobileLg: deviceBreakpoint === Breakpoints.mobileLg
    }
  }
)

export const DeviceLayoutContext = React.createContext<
  BreakpointType | undefined
>(Breakpoints.desktopMd)

export const useDeviceLayout = (): DeviceLayoutHookReturnType => {
  const deviceBreakpoint = useContext(DeviceLayoutContext)
  const output = breakPointToHookValue(deviceBreakpoint)
  useDebugValue(output)
  return output
}

export default DeviceLayoutContext.Consumer

// Minimize snapshot race-conditions with resizing
const THROTTLING_INTERVAL = isChromatic() ? 0 : 200 // ms

interface DeviceLayoutProviderProps {
  children: React.ReactNode
  value?: BreakpointType
  /**
   * If true, the device layout will be updated on mount.
   * This is useful for server-side rendering.
   */
  updateOnMount?: boolean
}

export const DeviceLayoutProvider = ({
  children,
  value = getDeviceBreakpointLayout(),
  updateOnMount = false
}: DeviceLayoutProviderProps) => {
  const [layout, setLayout] = useState(value)

  useEffect(() => {
    const updateResize = () => {
      // Workaround for full page screenshot issues with Playwright / Chromium
      // https://issues.chromium.org/issues/40256133
      if (window?.innerWidth < 10) return

      setLayout(getDeviceBreakpointLayout())
    }

    if (updateOnMount) {
      updateResize()
    }

    const throttledFn = throttle(updateResize, THROTTLING_INTERVAL)

    window.addEventListener('resize', throttledFn)
    return () => {
      window.removeEventListener('resize', throttledFn)
    }
  }, [])

  return (
    <DeviceLayoutContext.Provider value={layout}>
      {children}
    </DeviceLayoutContext.Provider>
  )
}
